import { ButtonLink } from '@klear/ui/components/button-link'
import { PlusIcon } from 'lucide-react'
import type { ColumnDef } from '@tanstack/react-table'
import { COUNTRY_CODE_OPTIONS } from '@klear/constants/countries'
import { DataTable } from '@klear/ui/components/data-table'
import { DataTableColumnHeader } from '@klear/ui/components/data-table-column-header'
import { CurrencyReportSelector } from '@klear/ui/components/currency-report-selector'
import type { RouterOutputs } from '@klear/ui/lib/trpc'
import {
  ORGANIZATION_SIZES,
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ENTITY_CUSTOMER,
  PERMISSION_ENTITY_INVOICE,
  ROUTE_CUSTOMERS
} from '@klear/constants'
import { useMemo } from 'react'
import { useParams, Link } from 'react-router'
import { useTrader } from '@klear/ui/hooks/use-trader'
import { unicodeFlag } from '@klear/ui/lib/utils'
import { useCustomers, useCustomerMutations, useCustomerPurchaseOrderVolumes } from '@klear/customer-ui/use-customers'
import { useInvoices } from '@klear/ui/hooks/use-invoices'
import { usePurchaseOrders } from '@klear/ui/hooks/use-purchase-orders'
import { useCurrencyScopedReport } from '@klear/ui/hooks/use-currency-scoped-report'
import { Money } from '@klear/money'
import RowActions from '@/components/row-actions'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { GrowingCustomersCard, DecliningCustomersCard } from '@/components/customer-growth-cards'
import { useDocumentTitle } from '@klear/ui/hooks/use-document-title'

type CustomerRow = RouterOutputs['customer']['list'][number]

const filters = [
  {
    title: 'Country',
    column: 'country',
    options: COUNTRY_CODE_OPTIONS
  }
]

const createColumns = (
  urlPrefixApp: string,
  deleteMutation: (params: { id: number }) => Promise<unknown>,
  isDeleting: boolean,
  volumesByCustomerId: Map<number, Money>
) => {
  const columns: ColumnDef<CustomerRow>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization name" />,
      cell: ({ row }) => <div className="">{row.original.name}</div>,
      enableSorting: true,
      enableHiding: true
    },
    {
      accessorKey: 'size',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization size" />,
      cell: ({ row }) => {
        const label = ORGANIZATION_SIZES.find((size) => size === row.original.size)

        return (
          <div className="flex space-x-2">
            <span className="max-w-[500px] truncate font-medium">{label}</span>
          </div>
        )
      }
    },
    {
      id: 'country',
      accessorFn: (row) => row.country,
      header: ({ column }) => <DataTableColumnHeader column={column} title="Organization country" />,
      cell: ({ row }) => {
        const countryCode = row.original.country
        const countryFlag = unicodeFlag(countryCode)

        return (
          <div className="flex items-center gap-2">
            <span>{countryFlag}</span>
            <span>{countryCode}</span>
          </div>
        )
      },
      filterFn: (row, _id, value) => value.includes(row.original.country),
      enableSorting: true,
      enableHiding: true
    },
    {
      id: 'purchaseOrderVolume',
      accessorFn: (row) => {
        const volume = row.id != null ? volumesByCustomerId.get(row.id) : undefined
        return volume != null ? volume.toString() : ''
      },
      header: ({ column }) => <DataTableColumnHeader column={column} title="Purchase Order Volume" />,
      cell: ({ row }) => {
        const volume = row.original.id != null ? volumesByCustomerId.get(row.original.id) : undefined
        return <div>{volume != null ? volume.toString() : '—'}</div>
      },
      enableSorting: true,
      enableHiding: true,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.id != null ? volumesByCustomerId.get(rowA.original.id) : undefined
        const b = rowB.original.id != null ? volumesByCustomerId.get(rowB.original.id) : undefined
        if (a == null) return b == null ? 0 : -1
        if (b == null) return 1
        return a.compare(b)
      }
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <RowActions
          row={row}
          appPath={`${urlPrefixApp}/${ROUTE_CUSTOMERS}`}
          resourceType={PERMISSION_ENTITY_CUSTOMER}
          canEdit={false}
          getResourceName={(customer) => customer.name}
          deleteMutation={deleteMutation}
          isDeleting={isDeleting}
        />
      )
    }
  ]

  return columns
}

export default function Customers() {
  useDocumentTitle('Customers')
  const { traderId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  const traderIdNum = parseInt(traderId || '0')
  const { user } = useAuth()

  const { invoices } = useInvoices({ traderId: traderIdNum })
  const { purchaseOrders } = usePurchaseOrders({ traderId: traderIdNum })

  const invoiceMonies = useMemo(
    () => invoices.map((inv) => inv.amount).filter((money): money is Money => money != null),
    [invoices]
  )

  const poMonies = useMemo(
    () => purchaseOrders.map((po) => po.amount).filter((money): money is Money => money != null),
    [purchaseOrders]
  )

  const { selectedCurrency, setSelectedCurrency, showCurrencySelector, availableCurrencies } = useCurrencyScopedReport([
    poMonies,
    invoiceMonies
  ])

  const { customers, isLoading } = useCustomers(traderIdNum)
  const customerIds = useMemo(
    () => customers.map((customer) => customer.id).filter((id): id is number => id != null),
    [customers]
  )
  const { volumesByCustomerId, isLoading: volumesLoading } = useCustomerPurchaseOrderVolumes(customerIds, {
    traderId: traderIdNum,
    currency: selectedCurrency
  })
  const { deleteCustomer, isDeleting } = useCustomerMutations()

  const showGrowthCards = user.hasPermission(PERMISSION_ENTITY_INVOICE, PERMISSION_ACTION_VIEW)

  const deleteCustomerWrapper = (params: { id: number }) =>
    deleteCustomer({
      id: params.id,
      traderId: traderIdNum
    })

  return (
    <>
      <div className="h-full flex-1 flex-col space-y-8 p-8 md:flex">
        <div className="flex items-center justify-between space-y-2">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Customers</h2>
            <p className="text-muted-foreground">View and manage your customers.</p>
          </div>
          <AddCustomerLink />
        </div>
        {showGrowthCards && (
          <div className="space-y-4">
            {showCurrencySelector && (
              <div className="flex items-center justify-end gap-3">
                <span className="text-sm text-muted-foreground">Insights currency</span>
                <CurrencyReportSelector
                  value={selectedCurrency}
                  onChange={setSelectedCurrency}
                  currencies={availableCurrencies}
                />
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              <GrowingCustomersCard traderId={traderIdNum} selectedCurrency={selectedCurrency} />
              <DecliningCustomersCard traderId={traderIdNum} selectedCurrency={selectedCurrency} />
            </div>
          </div>
        )}
        <DataTable
          data={customers}
          columns={createColumns(urlPrefixApp, deleteCustomerWrapper, isDeleting, volumesByCustomerId)}
          filters={filters}
          loading={isLoading || volumesLoading}
        />
      </div>
    </>
  )
}

const AddCustomerLink = () => {
  const { user } = useAuth()
  const { traderId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  if (!user.hasPermission(PERMISSION_ENTITY_CUSTOMER, PERMISSION_ACTION_ADD)) return null
  return (
    <ButtonLink Link={Link} to={`${urlPrefixApp}/${ROUTE_CUSTOMERS}/new`}>
      <PlusIcon className="mr-2 h-4 w-4" />
      Add Customer
    </ButtonLink>
  )
}
